/* Mēllo theme: core behaviour. No dependencies. */
(() => {
  const M = window.Mello || (window.Mello = {});
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const root = document.documentElement;

  /* ---------- Money ---------- */
  M.formatMoney = (cents) => {
    const amount = (Number(cents) || 0) / 100;
    if (M.moneyPrefix) {
      return M.moneyPrefix + amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    const fmt = M.moneyFormat || '{{amount}}';
    const two = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return fmt
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(amount).toLocaleString('en-US'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, two.replace(/,/g, ' ').replace('.', ','))
      .replace(/\{\{\s*amount\w*\s*\}\}/, two)
      .replace(/<[^>]*>/g, '');
  };

  /* ---------- Toast ---------- */
  let toastTimer;
  M.toast = (msg) => {
    const el = $('[data-toast]');
    if (!el) return;
    $('[data-toast-text]', el).textContent = msg;
    el.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-on'), 2800);
  };

  /* ---------- Overlays: cart, search, menu ---------- */
  const panels = { cart: 'cart-open', search: 'search-open', menu: 'menu-open' };
  let lastFocus = null;
  const panelEl = (name) => $(`[data-drawer="${name}"]`);
  M.open = (name) => {
    Object.keys(panels).forEach((n) => { if (n !== name) M.close(n, true); });
    lastFocus = document.activeElement;
    root.classList.add(panels[name]);
    document.body.classList.add('is-locked');
    $$(`[aria-controls][data-open="${name}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    const el = panelEl(name);
    if (el) {
      setTimeout(() => {
        const target = name === 'search' ? $('[data-search-input]', el) : el.querySelector('[data-close], a, button, input');
        (target || el).focus({ preventScroll: true });
      }, 60);
    }
  };
  M.close = (name, silent) => {
    if (!root.classList.contains(panels[name])) return;
    root.classList.remove(panels[name]);
    $$(`[aria-controls][data-open="${name}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (!Object.values(panels).some((c) => root.classList.contains(c))) document.body.classList.remove('is-locked');
    if (!silent && lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  };
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (opener && panels[opener.dataset.open]) {
      e.preventDefault();
      M.open(opener.dataset.open);
      return;
    }
    const closer = e.target.closest('[data-close]');
    if (closer && panels[closer.dataset.close]) {
      e.preventDefault();
      M.close(closer.dataset.close);
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') Object.keys(panels).forEach((n) => M.close(n));
  });

  /* ---------- Cart ---------- */
  const SECTION = 'cart-drawer';
  const setCount = (n) => {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = n;
      el.classList.remove('is-popping');
      void el.offsetWidth;
      el.classList.add('is-popping');
    });
  };
  const renderDrawer = (sections) => {
    const html = sections && sections[SECTION];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.getElementById(`shopify-section-${SECTION}`);
    const current = document.getElementById(`shopify-section-${SECTION}`);
    if (fresh && current) current.innerHTML = fresh.innerHTML;
    const aside = $('[data-drawer="cart"]');
    if (aside && aside.dataset.count != null) setCount(aside.dataset.count);
  };
  const drawerBusy = (on) => { const d = panelEl('cart'); if (d) d.classList.toggle('is-busy', on); };
  const postJSON = async (url, body) => {
    const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || M.strings.error);
    return data;
  };
  M.addToCart = async (items, { open = true } = {}) => {
    const data = await postJSON(`${M.routes.cartAdd}.js`, { items, sections: [SECTION], sections_url: window.location.pathname });
    renderDrawer(data.sections);
    if (open) M.open('cart');
    return data;
  };
  M.changeLine = async (line, quantity) => {
    drawerBusy(true);
    try {
      const data = await postJSON(`${M.routes.cartChange}.js`, { line, quantity, sections: [SECTION], sections_url: window.location.pathname });
      renderDrawer(data.sections);
    } catch (err) {
      M.toast(err.message);
    } finally {
      drawerBusy(false);
    }
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-drawer="cart"] [data-line]');
    if (!btn) return;
    e.preventDefault();
    M.changeLine(Number(btn.dataset.line), Math.max(0, Number(btn.dataset.qty)));
  });

  /* Quick add (cards, upsell) */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-quick-add]');
    if (!form) return;
    e.preventDefault();
    const card = form.closest('[data-card]');
    const fd = new FormData(form);
    const item = { id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) };
    if (fd.get('selling_plan')) item.selling_plan = Number(fd.get('selling_plan'));
    const label = card && $('[data-label]', card);
    if (card) { card.classList.add('is-added'); if (label) label.textContent = M.strings.added; }
    try {
      await new Promise((r) => setTimeout(r, card ? 380 : 0));
      await M.addToCart([item]);
    } catch (err) {
      M.toast(err.message);
    } finally {
      if (card) setTimeout(() => { card.classList.remove('is-added'); if (label) label.textContent = label.dataset.default || label.textContent; }, 1500);
    }
  });
  $$('[data-card] [data-label]').forEach((l) => { l.dataset.default = l.textContent; });

  /* ---------- Product form ---------- */
  class ProductForm {
    constructor(el) {
      this.el = el;
      const json = $('[data-product-json]', el);
      this.product = json ? JSON.parse(json.textContent) : null;
      if (!this.product) return;
      this.form = $('form', el);
      this.idInput = $('[data-variant-id]', el);
      this.planInput = $('[data-selling-plan]', el);
      this.qtyInput = $('[data-qty-input]', el);
      this.addBtn = $('[data-add-button]', el);
      this.addLabel = $('[data-add-label]', el);
      this.section = el.closest('.shopify-section') || document;
      this.variant = this.product.variants.find((v) => String(v.id) === String(this.idInput && this.idInput.value)) || this.product.variants[0];
      el.addEventListener('change', (e) => this.onChange(e));
      el.addEventListener('click', (e) => this.onClick(e));
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.update(false);
    }
    selectedOptions() {
      return $$('[data-option-index]', this.el).map((fs) => {
        const checked = $('input:checked', fs);
        return checked ? checked.value : null;
      });
    }
    purchase() {
      const p = $('[data-purchase]:checked', this.el);
      return p ? p.value : 'once';
    }
    planId() {
      const p = $('[data-plan]:checked', this.el);
      return p ? p.value : (this.planInput && this.planInput.dataset.default) || '';
    }
    onChange(e) {
      if (e.target.closest('[data-option-index]')) {
        const opts = this.selectedOptions();
        const match = this.product.variants.find((v) => v.options.every((o, i) => opts[i] == null || o === opts[i]));
        if (match) this.variant = match;
      }
      this.update(true);
    }
    onClick(e) {
      const dec = e.target.closest('[data-qty-dec]');
      const inc = e.target.closest('[data-qty-inc]');
      if (!dec && !inc) return;
      e.preventDefault();
      const q = Math.max(1, (Number(this.qtyInput.value) || 1) + (inc ? 1 : -1));
      this.qtyInput.value = q;
      this.update(false);
    }
    unitPrice() {
      const sub = this.purchase() === 'sub';
      if (sub) {
        const alloc = (this.variant.selling_plan_allocations || []).find((a) => String(a.selling_plan_id) === String(this.planId()));
        if (alloc) return alloc.price;
      }
      return this.variant.price;
    }
    update(fromUser) {
      const v = this.variant;
      if (!v) return;
      if (this.idInput) this.idInput.value = v.id;
      const sub = this.purchase() === 'sub';
      if (this.planInput) this.planInput.value = sub ? this.planId() : '';
      this.el.classList.toggle('is-sub', sub);
      const qty = Math.max(1, Number(this.qtyInput && this.qtyInput.value) || 1);
      const unit = this.unitPrice();
      $$('[data-price]', this.section).forEach((n) => { n.textContent = M.formatMoney(unit); });
      $$('[data-price-once]', this.section).forEach((n) => { n.textContent = M.formatMoney(v.price); });
      $$('[data-price-sub]', this.section).forEach((n) => {
        const alloc = (v.selling_plan_allocations || []).find((a) => String(a.selling_plan_id) === String(this.planId())) || (v.selling_plan_allocations || [])[0];
        if (alloc) n.textContent = M.formatMoney(alloc.price);
      });
      $$('[data-compare]', this.section).forEach((n) => {
        const show = sub && unit < v.price;
        n.hidden = !show;
        n.textContent = M.formatMoney(v.price);
      });
      $$('[data-variant-title]', this.section).forEach((n) => { n.textContent = v.options.length > 1 ? v.options[0] : (v.title === 'Default Title' ? '' : v.title); });
      if (this.addBtn) {
        this.addBtn.disabled = !v.available;
        if (this.addLabel && !this.adding) {
          this.addLabel.textContent = v.available ? `${M.strings.addToBag} · ${M.formatMoney(unit * qty)}` : M.strings.soldOut;
        }
      }
      // Purchase option cards
      $$('[data-purchase]', this.el).forEach((r) => r.closest('[data-purchase-card]') && r.closest('[data-purchase-card]').classList.toggle('is-on', r.checked));
      if (fromUser) {
        if (v.featured_media) this.showMedia(v.featured_media.id);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
    showMedia(id) {
      const gallery = $('[data-gallery]', this.section);
      if (gallery && gallery.mello) gallery.mello.show(String(id));
    }
    async onSubmit(e) {
      e.preventDefault();
      if (this.adding || !this.variant || !this.variant.available) return;
      this.adding = true;
      const qty = Math.max(1, Number(this.qtyInput && this.qtyInput.value) || 1);
      const item = { id: this.variant.id, quantity: qty };
      if (this.purchase() === 'sub' && this.planId()) item.selling_plan = Number(this.planId());
      const props = {};
      new FormData(this.form).forEach((val, key) => {
        const m = key.match(/^properties\[(.+)\]$/);
        if (m && val) props[m[1]] = val;
      });
      if (Object.keys(props).length) item.properties = props;
      this.addBtn.classList.add('is-added');
      if (this.addLabel) this.addLabel.textContent = M.strings.addedToBag || M.strings.added;
      try {
        await new Promise((r) => setTimeout(r, 450));
        await M.addToCart([item]);
      } catch (err) {
        M.toast(err.message);
      } finally {
        setTimeout(() => {
          this.adding = false;
          this.addBtn.classList.remove('is-added');
          this.update(false);
        }, 1150);
      }
    }
  }

  /* Gallery: crossfading main image + thumbnails */
  class Gallery {
    constructor(el) {
      this.el = el;
      el.mello = this;
      el.addEventListener('click', (e) => {
        const t = e.target.closest('[data-thumb]');
        if (t) { e.preventDefault(); this.show(t.dataset.mediaId); }
      });
    }
    show(id) {
      const slide = $(`[data-slide][data-media-id="${id}"]`, this.el);
      if (!slide) return;
      $$('[data-slide]', this.el).forEach((s) => s.classList.toggle('is-active', s === slide));
      $$('[data-thumb]', this.el).forEach((t) => t.classList.toggle('is-active', t.dataset.mediaId === id));
    }
  }

  /* ---------- Accordions ---------- */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-acc-toggle]');
    if (!t) return;
    const item = t.closest('.acc-item');
    const group = t.closest('[data-accordion]');
    const opening = !item.classList.contains('is-open');
    if (group && group.dataset.accordion === 'single') $$('.acc-item.is-open', group).forEach((i) => { i.classList.remove('is-open'); const b = $('[data-acc-toggle]', i); if (b) b.setAttribute('aria-expanded', 'false'); });
    item.classList.toggle('is-open', opening);
    t.setAttribute('aria-expanded', String(opening));
  });

  /* ---------- Predictive search ---------- */
  const initSearch = () => {
    const box = $('[data-search]');
    if (!box) return;
    const input = $('[data-search-input]', box);
    const results = $('[data-search-results]', box);
    const hint = $('[data-search-hint]', box);
    const popular = results.innerHTML;
    let timer;
    let seq = 0;
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (!q) { results.innerHTML = popular; hint.textContent = M.strings.searchPopular; return; }
      timer = setTimeout(async () => {
        const mine = ++seq;
        try {
          const url = `${M.routes.predictiveSearch}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=9&resources[options][unavailable_products]=last`;
          const res = await fetch(url);
          const data = await res.json();
          if (mine !== seq) return;
          const products = ((data.resources && data.resources.results && data.resources.results.products) || [])
            .filter((p) => !(p.tags || []).includes('subscription-only'));
          hint.textContent = products.length ? M.strings.searchResults.replace('__N__', products.length) : M.strings.searchEmpty;
          results.innerHTML = products.map((p) => {
            const img = p.featured_image && p.featured_image.url ? `<img src="${esc(p.featured_image.url)}&width=160" alt="" loading="lazy">` : '';
            const price = M.formatMoney(Math.round(parseFloat(p.price) * 100));
            return `<a class="search-result" href="${esc(p.url)}"><span class="search-result__thumb">${img}</span><span class="search-result__text"><span class="search-result__name">${esc(p.title)}</span><span class="search-result__price mono">${esc(price)}</span></span></a>`;
          }).join('');
        } catch (err) {
          hint.textContent = M.strings.error;
        }
      }, 180);
    });
  };

  /* ---------- Reveal on scroll ---------- */
  let io;
  const observeReveal = (scope = document) => {
    const els = $$('[data-reveal]:not(.is-in)', scope);
    if (!('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-in')); return; }
    if (!io) {
      io = new IntersectionObserver((entries) => entries.forEach((en) => {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-in');
        io.unobserve(en.target);
      }), { threshold: 0.06, rootMargin: '0px 0px -4% 0px' });
    }
    els.forEach((el) => {
      const i = Math.min(Number(el.dataset.revealI || 0), 6);
      el.style.setProperty('--d', `${i * 90}ms`);
      io.observe(el);
    });
  };
  M.observeReveal = observeReveal;

  /* ---------- Header shadow ---------- */
  const header = $('[data-header]');
  const onScroll = () => { if (header) header.classList.toggle('is-scrolled', window.scrollY > 8); };
  window.addEventListener('scroll', onScroll, { passive: true });

  /* ---------- Init ---------- */
  M.init = (scope = document) => {
    $$('[data-product-form]', scope).forEach((el) => { if (!el.mello) el.mello = new ProductForm(el); });
    $$('[data-gallery]', scope).forEach((el) => { if (!el.mello) new Gallery(el); });
    observeReveal(scope);
    document.dispatchEvent(new CustomEvent('mello:init', { detail: { scope } }));
  };
  const boot = () => {
    onScroll();
    initSearch();
    M.init();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', (e) => M.init(e.target));
})();
